// Generated by scripts/sportsbook_history/generate.mjs
import { useEffect, useMemo, useState } from "react";

export const CASINO_BETTING_HISTORY_PAGE_LIMIT = 10;

const TAB_GAME_TYPE = Object.freeze({
  Slots: "slots",
  "Live Casino": "live-casino",
  Sports: "live-sports",
  Minigames: "minigame",
});

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

const toKstDate = (date) =>
  new Date(date.getTime() + KST_OFFSET_MS).toISOString().slice(0, 10);

export const buildCasinoBettingHistoryParams = ({
  tab,
  dateRange,
  customStartDate,
  customEndDate,
  page,
  limit = CASINO_BETTING_HISTORY_PAGE_LIMIT,
  now = new Date(),
}) => {
  const today = toKstDate(now);
  const params = {
    reason: "BET",
    order_by_name: "id",
    order_by_type: "DESC",
    page,
    limit,
  };

  let startDate = today;
  let endDate = today;
  if (dateRange === "7days") {
    startDate = toKstDate(new Date(now.getTime() - 6 * DAY_MS));
  } else if (dateRange === "30days") {
    startDate = toKstDate(new Date(now.getTime() - 29 * DAY_MS));
  } else if (dateRange === "custom") {
    startDate = customStartDate || today;
    endDate = customEndDate || today;
  }

  params.start_date = `${startDate} 00:00:00`;
  params.end_date = `${endDate} 23:59:59`;
  if (TAB_GAME_TYPE[tab]) params["$bet.game_type$"] = TAB_GAME_TYPE[tab];
  return params;
};

const getRows = (response) => {
  const data = response?.data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data)) return data;
  return [];
};

export const useCasinoBettingHistoryQuery = ({
  service,
  tab,
  dateRange,
  customStartDate,
  customEndDate,
}) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const params = useMemo(
    () =>
      buildCasinoBettingHistoryParams({
        tab,
        dateRange,
        customStartDate,
        customEndDate,
        page,
      }),
    [customEndDate, customStartDate, dateRange, page, tab]
  );

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setLoading(true);
    setError("");

    service
      .bettingHistory(params, { signal: controller.signal })
      .then((response) => {
        if (!active || controller.signal.aborted) return;
        if (response?.status !== true) {
          throw new Error(response?.message || "베팅내역을 불러오지 못했습니다.");
        }
        const nextRows = getRows(response);
        setRows(nextRows);
        setTotalCount(Math.max(0, +(response?.data?.count ?? nextRows.length) || 0));
      })
      .catch((requestError) => {
        if (!active || controller.signal.aborted) return;
        setRows([]);
        setTotalCount(0);
        setError(requestError?.message || "베팅내역을 불러오지 못했습니다.");
      })
      .finally(() => {
        if (active && !controller.signal.aborted) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [params, service]);

  const totalPages = Math.max(1, Math.ceil(totalCount / CASINO_BETTING_HISTORY_PAGE_LIMIT));

  return {
    rows,
    totalCount,
    totalPages,
    page,
    setPage,
    loading,
    error,
    pageLimit: CASINO_BETTING_HISTORY_PAGE_LIMIT,
  };
};
